import React, { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import api from "../../../../api/client";
import MetricCard from "../../components/MetricCard";

function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((row) => headers.map((h) => JSON.stringify(row[h] ?? "")).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const inputStyle = {
  padding: "6px 10px", border: "1px solid #d6dfed", borderRadius: 7,
  fontSize: 13, color: "#374151", outline: "none", background: "#f8fafc",
};

export default function InventoryReportTab() {
  const todayStr = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [flow, setFlow] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [stagnantDays, setStagnantDays] = useState(30);
  const [stagnant, setStagnant] = useState([]);
  const [stagnantLoading, setStagnantLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [flowRes, lowStockRes, priceRes] = await Promise.all([
        api.get("/manufacturing/inventory/reports/inflow-outflow", { params: { dateFrom, dateTo } }),
        api.get("/manufacturing/inventory/reports/low-stock"),
        api.get("/manufacturing/inventory/reports/price-history"),
      ]);
      setFlow(flowRes.data.data);
      setLowStock(lowStockRes.data.data || []);
      setPriceHistory(priceRes.data.data || []);
    } catch {
      setFlow(null);
      setLowStock([]);
      setPriceHistory([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const loadStagnant = useCallback(async () => {
    setStagnantLoading(true);
    try {
      const res = await api.get("/manufacturing/inventory/reports/stagnant", { params: { days: stagnantDays } });
      setStagnant(res.data.data || []);
    } catch {
      setStagnant([]);
    } finally {
      setStagnantLoading(false);
    }
  }, [stagnantDays]);

  useEffect(() => { loadStagnant(); }, [loadStagnant]);

  const formatLastMoved = (dateStr) => {
    if (!dateStr) return "Never moved";
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    return `${new Date(dateStr).toLocaleDateString("en-IN")} (${days} day${days !== 1 ? "s" : ""} ago)`;
  };

  const handleExportReport = () => {
    const movementRows = (flow?.movements ?? []).map((m) => ({
      Date: new Date(m.createdAt).toLocaleString("en-IN"),
      Part: m.partMaster?.name,
      Type: m.type.toUpperCase(),
      Quantity: m.quantity,
      "Unit Price": m.unitPrice ?? "",
      Reason: m.reason,
    }));
    const lowStockRows = lowStock.map((p) => ({
      Part: p.name,
      Code: p.code,
      "Current Qty": p.currentStock,
      "Min Level": p.minStockLevel,
      Unit: p.unitOfMeasure,
    }));
    const stagnantRows = stagnant.map((p) => ({
      Part: p.name,
      Code: p.code,
      Category: p.category?.name || "",
      "Current Stock": p.currentStock,
      Unit: p.unitOfMeasure,
      "Last Movement": formatLastMoved(p.lastMovementAt),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(movementRows), "Movements");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lowStockRows), "Low Stock");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(stagnantRows), "Stagnant Stock");
    XLSX.writeFile(wb, `inventory-report-${dateFrom}_to_${dateTo}.xlsx`);
  };

  const handleExportPriceHistory = () => {
    if (!priceHistory.length) return;
    const rows = priceHistory.map((m) => ({
      Date: new Date(m.createdAt).toLocaleDateString("en-IN"),
      Part: m.partMaster?.name,
      Quantity: m.quantity,
      "Unit Price": m.unitPrice,
    }));
    downloadCSV(rows, `price-history-${todayStr}.csv`);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 13, color: "#64748b" }}>From</label>
          <input type="date" style={inputStyle} value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} />
          <label style={{ fontSize: 13, color: "#64748b" }}>To</label>
          <input type="date" style={inputStyle} value={dateTo} min={dateFrom} max={todayStr} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <button
          className="btn-primary"
          style={{ fontSize: 13 }}
          onClick={handleExportReport}
          disabled={!flow?.movements?.length && !lowStock.length && !stagnant.length}
        >
          Export Report
        </button>
      </div>

      <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <MetricCard label="Total Inflow" value={loading ? "—" : flow?.totalIn?.toLocaleString()} accent="#16a34a" />
        <MetricCard label="Total Outflow" value={loading ? "—" : flow?.totalOut?.toLocaleString()} accent="#dc2626" />
        <MetricCard label="Parts Low on Stock" value={loading ? "—" : lowStock.length} accent={lowStock.length > 0 ? "#d97706" : undefined} />
      </div>

      <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
        <div style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9" }}>
          <h2 style={{ margin: 0, fontSize: 15, color: "#0f2d5c" }}>Movements ({dateFrom} to {dateTo})</h2>
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
        ) : !flow?.movements?.length ? (
          <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>No movements in this range.</div>
        ) : (
          <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Date", "Part", "Type", "Qty", "Price", "Reason"].map((h) => (
                    <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", whiteSpace: "nowrap", position: "sticky", top: 0, background: "#f8fafc" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {flow.movements.map((m) => (
                  <tr key={m.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 16px", color: "#64748b" }}>{new Date(m.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td style={{ padding: "10px 16px", color: "#0f2d5c", fontWeight: 600 }}>{m.partMaster?.name}</td>
                    <td style={{ padding: "10px 16px", fontWeight: 700, color: m.type === "in" ? "#15803d" : "#dc2626" }}>{m.type.toUpperCase()}</td>
                    <td style={{ padding: "10px 16px" }}>{m.quantity}</td>
                    <td style={{ padding: "10px 16px", color: "#64748b" }}>{m.unitPrice != null ? `₹${m.unitPrice}` : "—"}</td>
                    <td style={{ padding: "10px 16px", color: "#64748b" }}>{m.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ background: "#fff", border: "1px solid #fca5a5", borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
        <div style={{ padding: "12px 20px", borderBottom: "1px solid #fca5a5", background: "#fff5f5" }}>
          <h2 style={{ margin: 0, fontSize: 15, color: "#dc2626" }}>Low Stock Report</h2>
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
        ) : lowStock.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "#15803d", fontWeight: 600 }}>No parts currently below their minimum stock level. ✓</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#fef2f2" }}>
                {["Part", "Code", "Current Qty", "Min Level"].map((h) => (
                  <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #fca5a5" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lowStock.map((p) => (
                <tr key={p.id} style={{ borderBottom: "1px solid #fef2f2" }}>
                  <td style={{ padding: "10px 16px", fontWeight: 600, color: "#0f2d5c" }}>{p.name}</td>
                  <td style={{ padding: "10px 16px", color: "#64748b", fontFamily: "monospace" }}>{p.code}</td>
                  <td style={{ padding: "10px 16px", color: "#dc2626", fontWeight: 700 }}>{p.currentStock} {p.unitOfMeasure}</td>
                  <td style={{ padding: "10px 16px" }}>{p.minStockLevel != null ? `${p.minStockLevel} ${p.unitOfMeasure}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ background: "#fff", border: "1px solid #f59e0b", borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
        <div style={{ padding: "12px 20px", borderBottom: "1px solid #f59e0b", background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, color: "#92400e" }}>Stagnant Stock</h2>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#92400e" }}>Parts with no logged movement in the selected window.</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 13, color: "#92400e" }}>No movement for</label>
            <input
              type="number" min="1" style={{ ...inputStyle, width: 70, background: "#fff" }}
              value={stagnantDays}
              onChange={(e) => setStagnantDays(Math.max(1, Number(e.target.value) || 1))}
            />
            <label style={{ fontSize: 13, color: "#92400e" }}>days</label>
          </div>
        </div>
        {stagnantLoading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
        ) : stagnant.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "#15803d", fontWeight: 600 }}>
            Every part has moved within the last {stagnantDays} day{stagnantDays !== 1 ? "s" : ""}. ✓
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#fffbeb" }}>
                  {["Part", "Code", "Category", "Current Stock", "Last Movement"].map((h) => (
                    <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #f59e0b" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stagnant.map((p) => (
                  <tr key={p.id} style={{ borderBottom: "1px solid #fef3c7" }}>
                    <td style={{ padding: "10px 16px", fontWeight: 600, color: "#0f2d5c" }}>{p.name}</td>
                    <td style={{ padding: "10px 16px", color: "#64748b", fontFamily: "monospace" }}>{p.code}</td>
                    <td style={{ padding: "10px 16px", color: "#64748b" }}>{p.category?.name || "—"}</td>
                    <td style={{ padding: "10px 16px" }}>{p.currentStock} {p.unitOfMeasure}</td>
                    <td style={{ padding: "10px 16px", color: p.lastMovementAt ? "#92400e" : "#dc2626", fontWeight: p.lastMovementAt ? 400 : 700 }}>
                      {formatLastMoved(p.lastMovementAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, color: "#0f2d5c" }}>Price History</h2>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>Every priced purchase receipt, most recent first.</p>
          </div>
          <button className="btn-primary" style={{ fontSize: 13 }} onClick={handleExportPriceHistory} disabled={!priceHistory.length}>
            Export CSV
          </button>
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
        ) : priceHistory.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>No priced purchases logged yet.</div>
        ) : (
          <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Date", "Part", "Qty", "Price"].map((h) => (
                    <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", position: "sticky", top: 0, background: "#f8fafc" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {priceHistory.map((m) => (
                  <tr key={m.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 16px", color: "#64748b" }}>{new Date(m.createdAt).toLocaleDateString("en-IN")}</td>
                    <td style={{ padding: "10px 16px", color: "#0f2d5c", fontWeight: 600 }}>{m.partMaster?.name}</td>
                    <td style={{ padding: "10px 16px" }}>{m.quantity}</td>
                    <td style={{ padding: "10px 16px", fontWeight: 700, color: "#1d4ed8" }}>₹{m.unitPrice}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
